import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { prequalCallApi } from "@/api/prequalCall";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { prequalKeys } from "@/hooks/prequalification/prequalKeys";
import { communicationKeys } from "@/pages/communications/hooks/useCommunications";

/**
 * Reads and writes for the review screen of a prequalification taken on a call
 * (ruling D10: review, then send).
 *
 * <p>🔴 <b>A decision moves three families at once.</b> Sending (or recording
 * "Don't send") changes the call state and this review (`buyerKeys.all`, which
 * also carries the education stamp a pass send sets), the prequalification's
 * own reads — its activity and emailed state (`prequalKeys.all`) — and, for a
 * send, the buyer's Communications tab (`communicationKeys.all`). Each mutation
 * invalidates all three inside its own options block, which is where
 * `staleAfterSaveGate` looks.</p>
 */

/** The review: the result, the documents, the email as it would go, or the
 *  decision once one is made. Read only while the drawer is open. */
export function usePrequalCallReview(
  buyerId: string,
  submissionId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: buyerKeys.prequalCallReview(buyerId, submissionId),
    queryFn: () => prequalCallApi.getReview(buyerId, submissionId),
    enabled: enabled && Boolean(buyerId) && Boolean(submissionId),
  });
}

function invalidateAfterDecision(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: buyerKeys.all });
  void qc.invalidateQueries({ queryKey: prequalKeys.all });
  void qc.invalidateQueries({ queryKey: communicationKeys.all });
}

/** Pass: send the combined email. Decline: send the decline documents. The
 *  composed subject and body are sent as they are — the screen offers no edit. */
export function useSendPrequalCall(buyerId: string, submissionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => prequalCallApi.send(buyerId, submissionId, {}),
    onSuccess: () => invalidateAfterDecision(qc),
  });
}

/** Decline only: record "Don't send". Nothing is emailed. */
export function useWithholdPrequalCall(buyerId: string, submissionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => prequalCallApi.withhold(buyerId, submissionId),
    onSuccess: () => invalidateAfterDecision(qc),
  });
}
